import { AccessGate } from '../../shared/access/AccessControls';
import { useAllowedMutation, withAccess } from '../../shared/access/AccessControls';
import React, { useEffect, useState } from 'react';
import {
  Button,
  Flex,
  Modal,
  ModalBody,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Input,
  useDisclosure,
  Textarea,
  Alert,
  Grid,
  Spacer,
  Divider,
  Text,
} from '@chakra-ui/react';
import { SubmitHandler, useFieldArray, useForm } from 'react-hook-form';
import { StyledNamesWrapper } from './styled';
import { integrationDefinitionsApi } from '../../store/services/IntegrationDefinitionsService';
import {
  IntegrationDefinitionSummary,
  IntegrationDefinition,
} from '../../shared/types/integrationConfiguration';
import FormInput from '../../shared/components/FormInput';

type IntegrationDefinitionModalProps = {
  role?: IntegrationDefinitionSummary | null;
  activator: React.ReactNode;
};
type FormDataType = {
  name: string;
  description: string;
  globalAttributes: { key: string; value: string }[];
};

// Mount a fresh form for each opening/id: an earlier hydration or successful
// mutation must never authorize saving another definition (or close its modal).
const IntegrationDefinitionForm = ({
  role,
  onClose,
}: {
  role: IntegrationDefinitionSummary | null;
  onClose: () => void;
}) => {
  const {
    currentData,
    isFetching,
    isError: isReadError,
    isUninitialized,
    refetch,
  } = integrationDefinitionsApi.useGetRoleByIdQuery(
    { roleId: role?.id ?? '' },
    { skip: !role?.id, refetchOnMountOrArgChange: true },
  );
  const [updateRole, { isLoading, isSuccess, isError }] =
    useAllowedMutation(integrationDefinitionsApi.useUpdateIntegrationDefinitionMutation, 'updateIntegrationDefinition');
  const [
    createRole,
    { isLoading: isCreating, isSuccess: isCreated, isError: isCreateError },
  ] = useAllowedMutation(integrationDefinitionsApi.useCreateIntegrationDefinitionMutation, 'createIntegrationDefinition');
  const { register, reset, handleSubmit, control } = useForm<FormDataType>({
    defaultValues: { name: '', description: '', globalAttributes: [] },
  });
  const {
    fields: attributes,
    append,
    remove,
  } = useFieldArray({ control, name: 'globalAttributes' });
  const [hydrated, setHydrated] = useState<IntegrationDefinition>();
  const isEditing = role !== null;
  const candidate = currentData?.role;
  const map = candidate?.globalAttributes;
  const configuration =
    role &&
    !isFetching &&
    !isReadError &&
    !isUninitialized &&
    candidate?.id === role.id &&
    typeof candidate?.name === 'string' &&
    (candidate?.description === undefined ||
      candidate?.description === null ||
      typeof candidate?.description === 'string') &&
    map &&
    typeof map === 'object' &&
    !Array.isArray(map) &&
    Object.values(map).every((value) => typeof value === 'string')
      ? candidate
      : undefined;
  const canSave =
    !isLoading &&
    !isCreating &&
    (!role || (!!configuration && hydrated === configuration));

  useEffect(() => {
    if (!isEditing) return; // Creating starts with an intentionally empty map.
    reset({
      name: configuration?.name ?? '',
      description: configuration?.description ?? '',
      globalAttributes: Object.entries(
        configuration?.globalAttributes ?? {},
      ).map(([key, value]) => ({ key, value })),
    });
    setHydrated(configuration);
  }, [isEditing, configuration, reset]);

  const onSubmit: SubmitHandler<FormDataType> = (args) => {
    // The handler protects Enter/programmatic submits as well as the button.
    // Never use the LIST object as a fallback for missing editor configuration.
    if (!canSave || !Array.isArray(args.globalAttributes)) return;
    const data = {
      name: args.name,
      description: args.description,
      globalAttributes: Object.fromEntries(
        args.globalAttributes.map(({ key, value }) => [key, value]),
      ),
    };
    if (role) updateRole({ roleId: role.id, data });
    else createRole({ data });
  };
  useEffect(() => {
    if (isCreated || isSuccess) onClose();
  }, [isCreated, isSuccess, onClose]);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <ModalHeader>
        {role ? 'Edit integration definition' : 'Create integration definition'}
      </ModalHeader>
      <ModalBody>
        {role && (
          <>
            {(isFetching || isUninitialized) && (
              <Text role="status">Loading integration configuration...</Text>
            )}
            {!isFetching && !isUninitialized && !configuration && (
              <Alert status="error">
                Could not load the integration definition. Saving is disabled.
              </Alert>
            )}
            <Button
              type="button"
              size="sm"
              mb="1rem"
              isDisabled={isFetching || isUninitialized}
              onClick={() => refetch()}
            >
              Reload configuration
            </Button>
          </>
        )}
        <fieldset
          disabled={!canSave}
          style={{ border: 0, padding: 0, margin: 0 }}
        >
          <StyledNamesWrapper>
            <label>Name</label>
            <Input
              placeholder="Enter integration name"
              mb="0.5rem"
              isRequired
              {...register('name')}
            />
            <label>Description</label>
            <Textarea
              placeholder="Enter integration description"
              mb="0.5rem"
              {...register('description')}
            />
            {!!attributes.length && (
              <Grid gap="2rem" my="1rem" w="100%">
                {attributes.map((attr, index) => (
                  <Grid key={attr.id} gap="0.5rem" gridTemplateColumns="1fr">
                    <FormInput
                      register={register}
                      ph="Key"
                      name={`globalAttributes.${index}.key`}
                    />
                    <FormInput
                      register={register}
                      ph="Value"
                      name={`globalAttributes.${index}.value`}
                    />
                    <Flex gap="1.5rem" alignItems="flex-end">
                      <Divider />
                      <Button
                        colorScheme="red"
                        type="button"
                        size="sm"
                        w="max-content"
                        onClick={() => remove(index)}
                      >
                        Delete
                      </Button>
                    </Flex>
                  </Grid>
                ))}
              </Grid>
            )}
            {(isError || isCreateError) && (
              <Alert status="error" borderRadius="0.5rem">
                Saving error
              </Alert>
            )}
          </StyledNamesWrapper>
        </fieldset>
      </ModalBody>
      <ModalFooter>
        <Flex gridGap="0.5rem" w="100%">
          <Button
            type="button"
            w="max-content"
            isDisabled={!canSave}
            onClick={() => append({ key: '', value: '' })}
          >
            Add attribute
          </Button>
          <Spacer />
          <Button type="button" onClick={onClose}>
            Cancel
          </Button>
          <AccessGate action={["ADMIN_ROLE_UPDATE","ADMIN_ROLE_CREATE"]}><Button
            colorScheme="blue"
            type="submit"
            isLoading={isLoading || isCreating}
            isDisabled={!canSave}
          >
            Save
          </Button></AccessGate>
        </Flex>
      </ModalFooter>
    </form>
  );
};

const IntegrationDefinitionModal = ({
  role = null,
  activator,
}: IntegrationDefinitionModalProps) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  return (
    <>
      <div role="button" onClick={onOpen}>
        {activator}
      </div>
      <Modal size="xl" {...{ onClose, isOpen }}>
        <ModalOverlay />
        <ModalContent mx="1rem">
          {isOpen && (
            <IntegrationDefinitionForm
              key={role?.id ?? 'new'}
              role={role}
              onClose={onClose}
            />
          )}
        </ModalContent>
      </Modal>
    </>
  );
};
export default withAccess(IntegrationDefinitionModal, props => props.role ? 'ADMIN_ROLE_UPDATE' : 'ADMIN_ROLE_CREATE');
